import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { TableProps } from 'antd';
import { Input, Typography } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import type { ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { tokens } from '../theme';

type TableDensity = 'large' | 'middle' | 'small';

/**
 * Bảng dùng chung của Admin Web — `ProTable` bọc lại để mọi bảng có cùng thanh công cụ
 * (tìm kiếm · tải lại · mật độ · ẩn/hiện cột · toàn màn hình) và cùng mặc định (ADR-009).
 *
 * <p>📌 <b>Bật đủ bộ công cụ mặc định của `ProTable`</b> (đổi 2026-09-02) — tải lại, mật độ,
 * ẩn/hiện cột và toàn màn hình đều dùng icon gốc của ProTable (`options`), thư viện tự quản
 * trạng thái. Riêng ô tìm kiếm là bản tự dựng bên trái (lọc client-side trên dữ liệu đã có sẵn
 * trong TanStack Query), nên `options.search` để tắt. Bản trước (01/09) từng gộp tải lại + mật độ
 * vào một nút dropdown và bỏ ẩn/hiện cột; nay mở lại đầy đủ theo yêu cầu.</p>
 *
 * <p>📌 <b>Nhận đúng props của `antd` Table.</b> Đổi một trang chỉ là `<Table` → `<DataTable`,
 * không phải viết lại cột. Chủ ý: 18 chỗ dùng bảng nằm rải khắp 13 file, và một cuộc đổi cần
 * sửa từng cột là một cuộc đổi sẽ làm dở dang.</p>
 *
 * <h4>🔴 Vì sao phải có lớp chuyển chữ ký `render`</h4>
 * <p>`ProTable` đổi tham số đầu của `render`: antd đưa <b>giá trị ô</b>, ProTable đưa
 * <b>DOM đã render sẵn</b>. Trong repo có <b>20 cột</b> đang dùng tham số đầu đó —
 * <c>render: (p: number) =&gt; p.toLocaleString('vi-VN')</c> chẳng hạn. Đổi thẳng thì chúng nhận
 * một ReactNode và hỏng <b>âm thầm</b>: không lỗi biên dịch, chỉ ra số sai hoặc "[object Object]"
 * trên màn hình thật.</p>
 * <p>Nên lớp dưới đây bọc lại từng `render` để nó vẫn nhận `(value, record, index)` đúng như antd.
 * Sửa một chỗ thay vì hai mươi, và người viết trang sau không phải nhớ ngoại lệ nào.</p>
 */

type AnyRecord = Record<string, unknown>;

export interface DataTableProps<T> extends Omit<TableProps<T>, 'columns' | 'title'> {
  columns: ColumnsType<T>;

  /**
   * Thanh công cụ phía trên bảng. Bật ở màn danh sách; **tắt** ở bảng nhỏ nằm trong ngăn kéo hay
   * hộp thoại — ở đó nút ẩn/hiện cột và mật độ là tiếng ồn, không phải tiện ích.
   */
  toolbar?: boolean;

  /** Hàm tải lại cho nút trên thanh công cụ. Không truyền thì nút không hiện. */
  onReload?: () => void;

  /** Nội dung thêm bên phải thanh công cụ (nút hành động của riêng trang). */
  toolbarExtra?: ReactNode;

  /**
   * Tiêu đề bảng, hiện bên TRÁI thanh công cụ (ô tìm/tải lại/mật độ nằm bên phải). Dùng khi bảng
   * đứng một mình cần một dòng nhận diện mà không phải bọc thêm một `Card` có title riêng.
   * Bỏ trống thì bên trái để trống như cũ.
   */
  title?: ReactNode;

  /** Dòng phụ mờ cạnh `title` — ví dụ phạm vi dữ liệu, ngày, số bản ghi. */
  subTitle?: ReactNode;

  /** Icon `?` cạnh `title` với chú thích khi hover. Chỉ có tác dụng khi có `title`. */
  titleTooltip?: string;

  /**
   * Tắt ô tìm kiếm riêng cho bảng này. Mặc định **bật** khi có thanh công cụ.
   *
   * Tắt khi bảng ngắn cố định (dưới ~10 dòng và không tăng) — ô tìm kiếm ở đó chỉ chiếm chỗ.
   */
  searchable?: boolean;

  /** Chữ gợi ý trong ô tìm kiếm. Đặt lại khi "bản ghi" có tên cụ thể hơn. */
  searchPlaceholder?: string;

  /**
   * Trả về chuỗi để dò tìm cho MỖI dòng — thay hẳn phép đoán cột theo `key`/`dataIndex`.
   *
   * <p>Dùng khi bảng có cột render GHÉP nhiều trường (tên + mã, ghi chú, nhãn loại…) mà heuristic
   * mặc định không thấy được: nó chỉ dò `row[dataIndex]` hoặc `row[key]`, nên cột `key='member'`
   * render `memberName`+`memberCode` sẽ không khớp gì. Khai `searchText` là gõ tên/mã/ghi chú đều ra.</p>
   */
  searchText?: (record: T) => string;

  /**
   * Bật chế độ tìm **SERVER-SIDE**: thay vì lọc client, ô tìm gọi hàm này — đã **debounce**, tự chạy
   * khi người dùng NGỪNG gõ, không cần nút. Trang dùng từ khóa để truy vấn API và tự lo phân trang
   * qua `pagination`; `dataSource` truyền vào đã là kết quả server nên DataTable KHÔNG lọc lại.
   * Bỏ trống thì tìm client-side như cũ.
   */
  onSearch?: (keyword: string) => void;

  /** Độ trễ debounce cho `onSearch` (ms). Mặc định 300 — đủ để không bắn mỗi phím. */
  searchDebounceMs?: number;
}

/** Lấy giá trị ô theo `dataIndex` đúng cách antd làm (chuỗi, mảng đường dẫn, hoặc không có). */
function valueAt<T>(record: T, dataIndex: ColumnType<T>['dataIndex']): unknown {
  if (dataIndex === undefined || dataIndex === null) return undefined;
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  let cur: unknown = record;
  for (const key of path) {
    if (cur === null || cur === undefined) return undefined;
    cur = (cur as AnyRecord)[key as string];
  }
  return cur;
}

/**
 * Bọc `render` của từng cột để nó nhận `(value, record, index)` như antd, thay vì
 * `(dom, entity, index)` như ProTable. Cột không có `render` thì giữ nguyên.
 */
function adaptColumns<T>(columns: ColumnsType<T>): ProColumns<T>[] {
  return columns.map((col) => {
    const c = col as ColumnType<T>;
    if (typeof c.render !== 'function') return c as ProColumns<T>;

    const original = c.render;
    const adapted: ProColumns<T>['render'] = (_dom, entity, index) =>
      original(valueAt(entity, c.dataIndex), entity, index) as ReactNode;

    return { ...c, render: adapted } as ProColumns<T>;
  });
}

export function DataTable<T extends object>({
  columns,
  toolbar = true,
  onReload,
  toolbarExtra,
  title,
  subTitle,
  titleTooltip,
  searchable = true,
  searchPlaceholder = 'Tìm trong bảng',
  searchText,
  onSearch,
  searchDebounceMs = 300,
  pagination,
  size = 'small',
  dataSource,
  ...rest
}: DataTableProps<T>) {
  const [keyword, setKeyword] = useState('');
  const [density, setDensity] = useState<TableDensity>(size ?? 'small');
  const showSearch = toolbar && searchable;
  const isServerSearch = typeof onSearch === 'function';

  // Debounce ô tìm khi ở chế độ server. Giữ `onSearch` trong ref để effect KHÔNG phụ thuộc identity
  // của nó — trang thường truyền arrow mới mỗi render, phụ thuộc vào đó thì mỗi lần onSearch đổi state
  // → re-render → effect chạy lại → tự bắn tìm lặp mãi. Chỉ chạy lại theo `keyword`.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const firstRun = useRef(true);
  useEffect(() => {
    if (!isServerSearch) return;
    // Bỏ qua lần chạy đầu (keyword rỗng lúc mount): trang đã tự tải trang đầu, không cần bắn thêm.
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const id = setTimeout(() => onSearchRef.current?.(keyword.trim()), searchDebounceMs);
    return () => clearTimeout(id);
  }, [keyword, isServerSearch, searchDebounceMs]);

  /**
   * Lọc ngay tại client. Dữ liệu đã nằm sẵn trong bộ nhớ (TanStack Query) nên gửi từ khoá lên
   * server chỉ thêm một vòng chờ mà không đổi kết quả.
   *
   * 📌 Dò trong những cột **đang hiện**, không dò cả object thô — gõ một chuỗi bất kỳ mà khớp một
   * `id` ẩn thì bảng trả về dòng trông chẳng liên quan gì, không giải thích được.
   *
   * 🔴 **Phải xét cả `key`, không chỉ `dataIndex`.** Bản đầu chỉ dò `dataIndex` và nó hỏng đúng ở
   * cột người ta muốn tìm nhất: cột tên thường viết `{ key: 'name', render: … }` **không có
   * `dataIndex`** (vì render ghép nhiều trường), nên gõ đúng tên chi nhánh vẫn ra 0 dòng. Đo trên
   * app thật mới lộ; đọc code thì thấy hợp lý.
   */
  const filtered = useMemo(() => {
    // Server lo việc lọc — `dataSource` đã đúng, không lọc lại ở client.
    if (isServerSearch) return dataSource;

    const kw = keyword.trim().toLowerCase();
    if (!kw || !Array.isArray(dataSource)) return dataSource;

    // Trang tự khai chuỗi dò → dùng thẳng, bỏ qua phép đoán cột.
    if (searchText) {
      return dataSource.filter((row) => searchText(row as T).toLowerCase().includes(kw));
    }

    const khop = (row: T, c: ColumnType<T>): boolean => {
      const ungVien: unknown[] = [];
      if (c.dataIndex !== undefined) ungVien.push(valueAt(row, c.dataIndex));
      // Cột render tùy biến: `key` thường chính là tên trường, dùng nó làm đường dò thứ hai.
      if (c.dataIndex === undefined && typeof c.key === 'string') {
        ungVien.push((row as AnyRecord)[c.key]);
      }
      return ungVien.some(
        (v) => v !== null && v !== undefined && String(v).toLowerCase().includes(kw),
      );
    };

    return dataSource.filter((row) => (columns as ColumnType<T>[]).some((c) => khop(row, c)));
  }, [dataSource, columns, keyword, searchText, isServerSearch]);

  // Dòng "x/y" chỉ có nghĩa khi lọc CLIENT; server-search thì tổng do server quyết, không đếm ở đây.
  const daLoc = !isServerSearch && keyword.trim().length > 0 && Array.isArray(dataSource);

  return (
    <ProTable<T>
      {...rest}
      dataSource={filtered}
      size={density}
      columns={adaptColumns(columns)}
      // Tiêu đề nằm bên TRÁI toolbar; chỉ có nghĩa khi toolbar đang bật. `subTitle` gộp thành
      // dòng phụ mờ ngay dưới vì ProTable không có prop subTitle ở cấp ngoài cùng.
      headerTitle={
        toolbar && (title != null || subTitle != null) ? (
          <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1.3 }}>
            {title != null && <span>{title}</span>}
            {subTitle != null && (
              <span style={{ fontSize: 12, fontWeight: 400, color: tokens.color.ink3 }}>
                {subTitle}
              </span>
            )}
          </span>
        ) : undefined
      }
      tooltip={toolbar ? titleTooltip : undefined}
      // Dữ liệu đã nằm sẵn ở client (TanStack Query), nên KHÔNG bật biểu mẫu truy vấn của
      // ProTable — nó sinh ra để gửi tham số lên server và ở đây chỉ là một khối trống chiếm chỗ.
      search={false}
      // Bật đủ bộ công cụ mặc định của ProTable: tải lại · mật độ · ẩn/hiện cột · toàn màn hình.
      // Ô tìm kiếm là bản tự dựng bên trái (client-side), nên tắt `search` của options.
      options={
        toolbar
          ? {
              reload: onReload ? () => onReload() : false,
              density: true,
              setting: true,
              fullScreen: true,
              search: false,
            }
          : false
      }
      // Mật độ do options quản, nhưng vẫn controlled qua `size`/`onSizeChange` để mặc định là "Gọn".
      onSizeChange={(s) => setDensity((s ?? 'small') as TableDensity)}
      toolBarRender={
        toolbar
          ? () => [
              showSearch ? (
                <Input
                  key="tim"
                  allowClear
                  size="small"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder={searchPlaceholder}
                  prefix={<MagnifyingGlass size={14} aria-hidden />}
                  aria-label={searchPlaceholder}
                  style={{ width: 220 }}
                />
              ) : null,
              // Nói RÕ đang lọc và còn bao nhiêu dòng. Thiếu câu này thì một bảng lọc còn 0 dòng
              // trông y hệt một bảng không có dữ liệu, và người dùng đi báo lỗi mất dữ liệu.
              daLoc ? (
                <Typography.Text key="dem" style={{ fontSize: 12, color: tokens.color.ink3 }}>
                  {(filtered as T[]).length}/{(dataSource as T[]).length} dòng
                </Typography.Text>
              ) : null,
              toolbarExtra,
            ]
          : false
      }
      // ProTable tự bọc bảng trong một Card. Các trang ở đây đã tự dựng Card/PageBody nên tắt đi,
      // không thì thành hai lớp viền lồng nhau.
      ghost
      cardProps={{ bodyStyle: { padding: 0 }, bordered: false }}
      pagination={
        pagination === false
          ? false
          : { pageSize: 20, showSizeChanger: false, ...(pagination ?? {}) }
      }
    />
  );
}
